import { useState, useMemo, useEffect } from 'react';
import { Calendar as BigCalendar, Views, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Sheet,
  Switch,
  FormControl,
  FormLabel,
  Menu,
  MenuButton,
  MenuItem,
  Dropdown,
} from '@mui/joy';
import { ViewWeek, CalendarViewMonth, Add, ViewDay, Person, Download, ArrowDropDown } from '@mui/icons-material';
import { ScheduleWithDetails } from '@/types/schedule';
import { Tag } from '@/types/tag';
import { TaskWithAssignees } from '@/types/project';
import { useAuth } from '@/context/AuthContext';
import toast from 'react-hot-toast';
import { noteService } from '@/services/noteService';
import { tagService } from '@/services/tagService';
import { NoteCounts } from '@/types/note';
import NotesModal from './NotesModal';
import { TagChip } from './TagChip';

const localizer = momentLocalizer(moment);

// Utility function to convert hex color to lighter version with opacity
const lightenColor = (hex: string, opacity: number = 0.15): string => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Return rgba string with opacity
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

interface CalendarProps {
  schedules: ScheduleWithDetails[];
  onScheduleClick?: (schedule: ScheduleWithDetails) => void;
  onAddSchedule?: () => void;
  scheduleTags?: Record<number, any[]>;
  onTagsChange?: () => void;
  /** Non-completed tasks visible to the current user (privacy-filtered by backend) */
  tasks?: TaskWithAssignees[];
  /** When true, matching tasks are shown embedded inside schedule events */
  showTasks?: boolean;
  onTaskClick?: (task: TaskWithAssignees) => void;
}

interface CalendarEvent {
  id: number | string;
  title: string;
  start: Date;
  end: Date;
  resource?: ScheduleWithDetails;
  taskResource?: TaskWithAssignees;
  color: string;
  allDay?: boolean;
}

// Stable color from project ID

export default function Calendar({ schedules, onScheduleClick, onAddSchedule, scheduleTags = {}, onTagsChange, tasks = [], showTasks = true, onTaskClick }: CalendarProps) {
  const { permissions, user } = useAuth();
  const [view, setView] = useState<any>(Views.WEEK);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showOnlyMySchedules, setShowOnlyMySchedules] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [noteCounts, setNoteCounts] = useState<NoteCounts>({});
  const [notesModalOpen, setNotesModalOpen] = useState<boolean>(false);
  const [selectedNoteDate, setSelectedNoteDate] = useState<string>('');

  // Helper function to check permissions
  const hasPermission = (feature: string, action: string) => {
    const permissionName = `${feature}.${action}`;
    return permissions.includes(permissionName);
  };

  // Fetch note counts for the current view
  const fetchNoteCounts = async (startDate: Date, endDate: Date) => {
    try {
      const startDateStr = moment(startDate).format('YYYY-MM-DD');
      const endDateStr = moment(endDate).format('YYYY-MM-DD');
      const counts = await noteService.getNoteCounts(startDateStr, endDateStr);
      setNoteCounts(counts);
    } catch (error) {
      console.error('Error fetching note counts:', error);
    }
  };

  // Handle note icon click
  const handleNoteIconClick = (date: string) => {
    setSelectedNoteDate(date);
    setNotesModalOpen(true);
  };

  // Handle note modal close
  const handleNotesModalClose = () => {
    setNotesModalOpen(false);
    setSelectedNoteDate('');
  };

  // Refresh note counts when notes are modified
  const handleNoteChange = () => {
    if (view === Views.MONTH) {
      const startOfMonth = moment(selectedDate).startOf('month').toDate();
      const endOfMonth = moment(selectedDate).endOf('month').toDate();
      fetchNoteCounts(startOfMonth, endOfMonth);
    } else if (view === Views.WEEK) {
      const startOfWeek = moment(selectedDate).startOf('week').toDate();
      const endOfWeek = moment(selectedDate).endOf('week').toDate();
      fetchNoteCounts(startOfWeek, endOfWeek);
    }
  };

  // Fetch note counts when view or date changes
  useEffect(() => {
    if (view === Views.MONTH) {
      const startOfMonth = moment(selectedDate).startOf('month').toDate();
      const endOfMonth = moment(selectedDate).endOf('month').toDate();
      fetchNoteCounts(startOfMonth, endOfMonth);
    } else if (view === Views.WEEK) {
      const startOfWeek = moment(selectedDate).startOf('week').toDate();
      const endOfWeek = moment(selectedDate).endOf('week').toDate();
      fetchNoteCounts(startOfWeek, endOfWeek);
    }
  }, [view, selectedDate]);

  // Add note icons and day click handlers to calendar cells after render
  useEffect(() => {
    if (view === Views.MONTH || view === Views.WEEK) {
      const addNoteIconsAndClickHandlers = () => {
        // Find all date cells in the calendar
        const dateCells = document.querySelectorAll('.rbc-date-cell');
        
        dateCells.forEach((cell) => {
          const dateElement = cell.querySelector('.rbc-button-link');
          if (dateElement) {
            const dateText = dateElement.textContent;
            if (dateText) {
              // Parse the date
              const currentYear = moment(selectedDate).year();
              const currentMonth = moment(selectedDate).month();
              const day = parseInt(dateText);
              
              if (!isNaN(day)) {
                const cellDate = moment([currentYear, currentMonth, day]);
                const dateStr = cellDate.format('YYYY-MM-DD');
                const noteCount = noteCounts[dateStr] || 0;
                
                // Remove existing note icons
                const existingIcon = cell.querySelector('.note-icon-container');
                if (existingIcon) {
                  existingIcon.remove();
                }
                
                // Add click handler to date cell for switching to day view
                if (view === Views.MONTH) {
                  // Remove existing click handler if any
                  const existingHandler = (cell as any).__dayClickHandler;
                  if (existingHandler) {
                    cell.removeEventListener('click', existingHandler);
                  }
                  
                  const clickHandler = (e: MouseEvent) => {
                    // Only trigger if clicking on the date cell itself, not on events
                    const target = e.target as HTMLElement;
                    if (target.closest('.rbc-event')) {
                      return; // Don't switch view if clicking on an event
                    }
                    setSelectedDate(cellDate.toDate());
                    setView('day');
                  };
                  
                  (cell as any).__dayClickHandler = clickHandler;
                  cell.addEventListener('click', clickHandler);
                }
                
                // Add note icon if there are notes
                if (noteCount > 0) {
                  const iconContainer = document.createElement('div');
                  iconContainer.className = 'note-icon-container';
                  iconContainer.style.cssText = `
                    position: absolute;
                    top: 2px;
                    right: 2px;
                    z-index: 10;
                    cursor: pointer;
                    opacity: 0.8;
                    background: rgba(255, 255, 255, 0.9);
                    border-radius: 4px;
                    padding: 2px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                  `;
                  
                  // Create the icon element
                  const iconElement = document.createElement('div');
                  iconElement.innerHTML = `
                    <div style="display: flex; align-items: center; gap: 2px; color: #3b82f6;">
                      <span style="font-size: 12px;">📝</span>
                      ${noteCount > 1 ? `<span style="font-size: 10px; font-weight: bold;">${noteCount}</span>` : ''}
                    </div>
                  `;
                  
                  iconContainer.appendChild(iconElement);
                  cell.appendChild(iconContainer);
                  
                  // Add hover effect
                  iconContainer.addEventListener('mouseenter', () => {
                    iconContainer.style.opacity = '1';
                    iconContainer.style.transform = 'scale(1.1)';
                  });
                  
                  iconContainer.addEventListener('mouseleave', () => {
                    iconContainer.style.opacity = '0.8';
                    iconContainer.style.transform = 'scale(1)';
                  });
                  
                  // Add click handler
                  iconContainer.addEventListener('click', (e) => {
                    e.stopPropagation();
                    handleNoteIconClick(dateStr);
                  });
                }
              }
            }
          }
        });
      };
      
      // Add icons and handlers after a short delay to ensure calendar is rendered
      const timer = setTimeout(addNoteIconsAndClickHandlers, 100);
      return () => {
        clearTimeout(timer);
        // Clean up event listeners
        const dateCells = document.querySelectorAll('.rbc-date-cell');
        dateCells.forEach((cell) => {
          const existingHandler = (cell as any).__dayClickHandler;
          if (existingHandler) {
            cell.removeEventListener('click', existingHandler);
            delete (cell as any).__dayClickHandler;
          }
        });
      };
    }
  }, [view, selectedDate, noteCounts, handleNoteIconClick]);

  // ICS Export function
  const exportToICS = async () => {
    setIsExporting(true);
    try {
      const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';
      const token = localStorage.getItem('accessToken');

      const viewType = view === 'day' ? 'day' : view === Views.WEEK ? 'week' : 'month';
      
      const response = await fetch(`${API_BASE_URL}/calendar/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          view: viewType,
          selected_date: selectedDate.toISOString(),
          my_schedules_only: showOnlyMySchedules,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export calendar');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers.get('content-disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') || `shiftifi-calendar-${viewType}.ics`
        : `shiftifi-calendar-${viewType}.ics`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Calendar exported successfully!');
    } catch (error: any) {
      console.error('Export failed:', error);
      toast.error(error.message || 'Failed to export calendar');
    } finally {
      setIsExporting(false);
    }
  };

  // PDF Export function — draws a structured, vector PDF (no screenshots)
  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const jsPDF = (await import('jspdf')).default;

      const isDayView = view === 'day';
      const isWeekView = view === Views.WEEK;
      const isMonthView = view === Views.MONTH;

      // ── 1. Compute date range ──
      let rangeStart: moment.Moment;
      let rangeEnd: moment.Moment;
      if (isMonthView) {
        rangeStart = moment(selectedDate).startOf('month').startOf('week');
        rangeEnd = moment(selectedDate).endOf('month').endOf('week');
      } else if (isWeekView) {
        rangeStart = moment(selectedDate).startOf('week');
        rangeEnd = moment(selectedDate).endOf('week');
      } else {
        rangeStart = moment(selectedDate).startOf('day');
        rangeEnd = moment(selectedDate).endOf('day');
      }
      const rangeStartStr = rangeStart.format('YYYY-MM-DD');
      const rangeEndStr = rangeEnd.format('YYYY-MM-DD');

      // ── 2. Filter + group schedules by YYYY-MM-DD ──
      const filteredSchedules = (showOnlyMySchedules && user
        ? schedules.filter(s => s.employee_id === user.id)
        : schedules
      ).filter(s => {
        const start = moment(s.start_datetime);
        return start.isSameOrAfter(rangeStart) && start.isSameOrBefore(rangeEnd);
      });

      const schedulesByDate = new Map<string, ScheduleWithDetails[]>();
      for (const s of filteredSchedules) {
        const key = moment(s.start_datetime).format('YYYY-MM-DD');
        const list = schedulesByDate.get(key) ?? [];
        list.push(s);
        schedulesByDate.set(key, list);
      }
      for (const list of schedulesByDate.values()) {
        list.sort((a, b) =>
          new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
        );
      }

      // ── 3. Fetch notes for range ──
      let notes: any[] = [];
      try {
        notes = isDayView
          ? await noteService.getNotesByDate(rangeStartStr)
          : await noteService.getNotesByDateRange(rangeStartStr, rangeEndStr);
      } catch (err) {
        console.error('Failed to load notes for export:', err);
      }
      notes.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

      // ── 4. Build PDF ──
      const pdf = new jsPDF(isDayView ? 'portrait' : 'landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;

      // Helpers for hex colors
      const hexToRgb = (hex: string): [number, number, number] => {
        const h = (hex || '#666666').replace('#', '');
        const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
        return [
          parseInt(full.substring(0, 2), 16) || 0,
          parseInt(full.substring(2, 4), 16) || 0,
          parseInt(full.substring(4, 6), 16) || 0,
        ];
      };
      const mixWithWhite = (rgb: [number, number, number], opacity: number): [number, number, number] => [
        Math.round(rgb[0] * opacity + 255 * (1 - opacity)),
        Math.round(rgb[1] * opacity + 255 * (1 - opacity)),
        Math.round(rgb[2] * opacity + 255 * (1 - opacity)),
      ];

      const fmtTime = (iso: string) => moment(iso).format('HH:mm');
      const getTags = (s: ScheduleWithDetails): Tag[] => {
        const raw = (scheduleTags[s.id] as any[]) || (s.tags as any[]) || [];
        return raw.map((t: any) => (t && t.tag ? t.tag : t)).filter(Boolean);
      };

      // ── Draw header ──
      const drawHeader = () => {
        let rangeLabel: string;
        if (isMonthView) {
          rangeLabel = moment(selectedDate).format('MMMM YYYY');
        } else if (isWeekView) {
          const wkStart = moment(selectedDate).startOf('week');
          const wkEnd = moment(selectedDate).endOf('week');
          rangeLabel = wkStart.isSame(wkEnd, 'month')
            ? `${wkStart.format('MMM D')} – ${wkEnd.format('D, YYYY')}`
            : `${wkStart.format('MMM D')} – ${wkEnd.format('MMM D, YYYY')}`;
        } else {
          rangeLabel = moment(selectedDate).format('dddd, MMMM D, YYYY');
        }

        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(18);
        pdf.text('Shiftifi Calendar', margin, margin + 6);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(12);
        pdf.text(rangeLabel, margin, margin + 13);

        pdf.setFontSize(9);
        pdf.setTextColor(90, 90, 90);
        const filterLine = showOnlyMySchedules && user
          ? `Filtered: ${user.first_name} ${user.last_name}'s schedules only`
          : 'All schedules';
        pdf.text(filterLine, margin, margin + 19);
        pdf.setTextColor(0, 0, 0);

        return margin + 24; // y after header
      };

      // Page pagination helper for flowing sections
      const ensureSpace = (y: number, needed: number): number => {
        if (y + needed > pageHeight - margin) {
          pdf.addPage();
          return drawHeader();
        }
        return y;
      };

      // Render a small tag pill (returns width consumed)
      const drawTagPill = (x: number, y: number, tag: Tag, maxWidth: number): number => {
        pdf.setFontSize(7);
        const label = tag.name || '';
        const textWidth = pdf.getTextWidth(label);
        const padX = 1.2;
        const pillW = Math.min(textWidth + padX * 2, maxWidth);
        const pillH = 3.2;
        const [r, g, b] = hexToRgb(tag.color || '#888888');
        const [br, bg, bb] = mixWithWhite([r, g, b], 0.25);
        pdf.setFillColor(br, bg, bb);
        pdf.setDrawColor(r, g, b);
        pdf.setLineWidth(0.1);
        pdf.roundedRect(x, y - pillH + 0.8, pillW, pillH, 0.6, 0.6, 'FD');
        pdf.setTextColor(r * 0.6, g * 0.6, b * 0.6);
        pdf.text(label, x + padX, y, { maxWidth: pillW - padX * 2 });
        pdf.setTextColor(0, 0, 0);
        return pillW;
      };

      // Render a schedule block within a cell, returns height consumed
      const drawShiftBlock = (
        x: number,
        y: number,
        w: number,
        s: ScheduleWithDetails,
        compact: boolean,
      ): number => {
        const color = s.shift_type?.color || '#666666';
        const [r, g, b] = hexToRgb(color);
        const [fr, fg, fb] = mixWithWhite([r, g, b], 0.2);

        const timeStr = `${fmtTime(s.start_datetime)}–${fmtTime(s.end_datetime)}`;
        const employee = `${s.employee?.first_name ?? ''} ${s.employee?.last_name ?? ''}`.trim();
        const shiftName = s.shift_type?.name || '';
        const tags = getTags(s);

        const lineHeight = compact ? 2.6 : 3.2;
        const innerPadX = 1.2;
        const innerPadY = 0.8;

        // Estimate height
        let lines = 1; // employee + time
        if (shiftName) lines += 1;
        let tagRows = 0;
        if (tags.length > 0) tagRows = 1;
        const totalH = innerPadY * 2 + lines * lineHeight + tagRows * 3.6;

        pdf.setFillColor(fr, fg, fb);
        pdf.setDrawColor(r, g, b);
        pdf.setLineWidth(0.2);
        pdf.roundedRect(x, y, w, totalH, 0.8, 0.8, 'FD');
        // Left accent bar
        pdf.setFillColor(r, g, b);
        pdf.rect(x, y, 0.9, totalH, 'F');

        const textX = x + innerPadX + 1.2;
        const textMaxW = w - (innerPadX + 1.2) - innerPadX;
        let ty = y + innerPadY + lineHeight - 0.4;

        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(compact ? 7 : 8);
        const line1 = `${timeStr}  ${employee}`;
        pdf.text(pdf.splitTextToSize(line1, textMaxW)[0], textX, ty);
        ty += lineHeight;

        if (shiftName) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(compact ? 6.5 : 7.5);
          pdf.text(pdf.splitTextToSize(shiftName, textMaxW)[0], textX, ty);
          ty += lineHeight;
        }

        if (tags.length > 0) {
          let tx = textX;
          const maxTagRowRight = x + w - innerPadX;
          let shown = 0;
          for (const tag of tags) {
            const remaining = maxTagRowRight - tx;
            if (remaining < 6) break;
            const consumed = drawTagPill(tx, ty + 1, tag, remaining);
            tx += consumed + 0.8;
            shown++;
          }
          if (shown < tags.length) {
            pdf.setFontSize(6.5);
            pdf.setTextColor(80, 80, 80);
            pdf.text(`+${tags.length - shown}`, tx, ty + 1);
            pdf.setTextColor(0, 0, 0);
          }
        }

        return totalH;
      };

      // ── Draw month grid ──
      const drawMonthGrid = (startY: number): number => {
        const totalDays = rangeEnd.diff(rangeStart, 'days') + 1;
        const rows = Math.ceil(totalDays / 7);
        const headerH = 5;
        const available = pageHeight - startY - margin - 2;
        const cellW = contentWidth / 7;
        const cellH = Math.max(18, (available - headerH) / rows);

        // Weekday headers
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, startY, contentWidth, headerH, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(60, 60, 60);
        for (let i = 0; i < 7; i++) {
          const label = moment(rangeStart).add(i, 'days').format('ddd');
          pdf.text(label, margin + i * cellW + cellW / 2, startY + 3.5, { align: 'center' });
        }

        // Grid
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.2);
        const gridTop = startY + headerH;
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < 7; c++) {
            const x = margin + c * cellW;
            const y = gridTop + r * cellH;
            pdf.rect(x, y, cellW, cellH);

            const dayMoment = moment(rangeStart).add(r * 7 + c, 'days');
            const dateKey = dayMoment.format('YYYY-MM-DD');
            const inCurrentMonth = dayMoment.isSame(selectedDate, 'month');

            // Date number
            pdf.setFont('helvetica', inCurrentMonth ? 'bold' : 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(inCurrentMonth ? 0 : 160, inCurrentMonth ? 0 : 160, inCurrentMonth ? 0 : 160);
            pdf.text(String(dayMoment.date()), x + 1.5, y + 3.5);

            // Shifts
            const dayShifts = schedulesByDate.get(dateKey) || [];
            let innerY = y + 4.5;
            const innerX = x + 1;
            const innerW = cellW - 2;
            const maxY = y + cellH - 1;
            let shown = 0;
            for (const s of dayShifts) {
              const estH = 3.2 + (s.shift_type?.name ? 2.6 : 0) + (getTags(s).length > 0 ? 3.6 : 0) + 1.6;
              if (innerY + estH > maxY) break;
              const consumed = drawShiftBlock(innerX, innerY, innerW, s, true);
              innerY += consumed + 0.8;
              shown++;
            }
            if (shown < dayShifts.length) {
              pdf.setFont('helvetica', 'italic');
              pdf.setFontSize(6.5);
              pdf.setTextColor(100, 100, 100);
              pdf.text(`+${dayShifts.length - shown} more`, x + 1.5, maxY - 0.5);
            }
            pdf.setTextColor(0, 0, 0);
          }
        }

        return gridTop + rows * cellH;
      };

      // ── Draw week grid ──
      const drawWeekGrid = (startY: number): number => {
        const headerH = 6;
        const available = pageHeight - startY - margin - 2;
        const cellW = contentWidth / 7;
        const cellH = Math.max(30, available - headerH);

        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, startY, contentWidth, headerH, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(40, 40, 40);
        for (let i = 0; i < 7; i++) {
          const m = moment(rangeStart).add(i, 'days');
          const label = m.format('ddd D');
          pdf.text(label, margin + i * cellW + cellW / 2, startY + 4, { align: 'center' });
        }

        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.2);
        const gridTop = startY + headerH;
        for (let c = 0; c < 7; c++) {
          const x = margin + c * cellW;
          const y = gridTop;
          pdf.rect(x, y, cellW, cellH);

          const dayMoment = moment(rangeStart).add(c, 'days');
          const dateKey = dayMoment.format('YYYY-MM-DD');
          const dayShifts = schedulesByDate.get(dateKey) || [];
          let innerY = y + 1.5;
          const innerX = x + 1;
          const innerW = cellW - 2;
          const maxY = y + cellH - 1;
          let shown = 0;
          for (const s of dayShifts) {
            const estH = 3.2 + (s.shift_type?.name ? 3.2 : 0) + (getTags(s).length > 0 ? 4.2 : 0) + 1.6;
            if (innerY + estH > maxY) break;
            const consumed = drawShiftBlock(innerX, innerY, innerW, s, false);
            innerY += consumed + 1;
            shown++;
          }
          if (shown < dayShifts.length) {
            pdf.setFont('helvetica', 'italic');
            pdf.setFontSize(7);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`+${dayShifts.length - shown} more`, x + 1.5, maxY - 0.5);
            pdf.setTextColor(0, 0, 0);
          }
        }

        return gridTop + cellH;
      };

      // ── Draw day list (shifts + notes inline) ──
      const drawDayList = (startY: number): number => {
        let y = startY;
        const dateKey = rangeStart.format('YYYY-MM-DD');
        const dayShifts = schedulesByDate.get(dateKey) || [];

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        y = ensureSpace(y, 8);
        pdf.text(`Shifts (${dayShifts.length})`, margin, y);
        y += 4;

        if (dayShifts.length === 0) {
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(10);
          pdf.setTextColor(120, 120, 120);
          y = ensureSpace(y, 6);
          pdf.text('No shifts scheduled.', margin, y + 4);
          pdf.setTextColor(0, 0, 0);
          y += 8;
        } else {
          for (const s of dayShifts) {
            const color = s.shift_type?.color || '#666666';
            const [r, g, b] = hexToRgb(color);
            const [fr, fg, fb] = mixWithWhite([r, g, b], 0.15);
            const tags = getTags(s);

            const employee = `${s.employee?.first_name ?? ''} ${s.employee?.last_name ?? ''}`.trim();
            const shiftName = s.shift_type?.name || '';
            const timeStr = `${fmtTime(s.start_datetime)} – ${fmtTime(s.end_datetime)}`;
            const status = s.status ? s.status.toUpperCase() : '';

            // Measure
            const blockW = contentWidth;
            const innerPadX = 3;
            const innerPadY = 2.5;
            const textMaxW = blockW - innerPadX * 2 - 2;

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(11);
            const titleLines = pdf.splitTextToSize(`${timeStr}   ${employee}`, textMaxW);

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9.5);
            const subtitleLines = shiftName
              ? pdf.splitTextToSize(`${shiftName}${status ? `  ·  ${status}` : ''}`, textMaxW)
              : [];

            const lineH = 5;
            const subLineH = 4.5;
            const tagRowH = tags.length > 0 ? 5 : 0;
            const estH = innerPadY * 2 + titleLines.length * lineH + subtitleLines.length * subLineH + tagRowH;

            y = ensureSpace(y, estH + 2);

            pdf.setFillColor(fr, fg, fb);
            pdf.setDrawColor(r, g, b);
            pdf.setLineWidth(0.3);
            pdf.roundedRect(margin, y, blockW, estH, 1.2, 1.2, 'FD');
            pdf.setFillColor(r, g, b);
            pdf.rect(margin, y, 1.5, estH, 'F');

            let ty = y + innerPadY + lineH - 0.5;
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(11);
            for (const line of titleLines) {
              pdf.text(line, margin + innerPadX + 2, ty);
              ty += lineH;
            }
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9.5);
            for (const line of subtitleLines) {
              pdf.text(line, margin + innerPadX + 2, ty);
              ty += subLineH;
            }
            if (tags.length > 0) {
              let tx = margin + innerPadX + 2;
              const rightEdge = margin + blockW - innerPadX;
              for (const tag of tags) {
                const remaining = rightEdge - tx;
                if (remaining < 8) break;
                const consumed = drawTagPill(tx, ty + 1, tag, remaining);
                tx += consumed + 1.2;
              }
            }

            y += estH + 2;
          }
        }

        // Notes for the day
        y += 4;
        y = ensureSpace(y, 10);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Notes (${notes.length})`, margin, y);
        y += 4;

        if (notes.length === 0) {
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(10);
          pdf.setTextColor(120, 120, 120);
          y = ensureSpace(y, 6);
          pdf.text('No notes for this day.', margin, y + 4);
          pdf.setTextColor(0, 0, 0);
          y += 8;
        } else {
          y = drawNotesList(y, notes, /* includeDateHeader */ false);
        }

        return y;
      };

      // ── Notes list (shared by month/week and day) ──
      const drawNotesList = (startY: number, items: any[], includeDateHeader: boolean): number => {
        let y = startY;
        pdf.setTextColor(0, 0, 0);

        let lastDate: string | null = null;
        for (const n of items) {
          if (includeDateHeader && n.date !== lastDate) {
            y = ensureSpace(y, 8);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            pdf.text(moment(n.date).format('dddd, MMMM D, YYYY'), margin, y + 4);
            y += 6;
            lastDate = n.date;
          }

          const priority: 'normal' | 'important' | 'urgent' = n.priority || 'normal';
          const priorityColor =
            priority === 'urgent' ? '#ef4444' : priority === 'important' ? '#f59e0b' : '#3b82f6';
          const [pr, pg, pb] = hexToRgb(priorityColor);

          const title = n.title ? String(n.title) : '';
          const content = n.content ? String(n.content) : '';
          const author = n.creator_name ? `— ${n.creator_name}` : '';

          const innerPadX = 3;
          const innerPadY = 2;
          const titleFS = 10;
          const bodyFS = 9;
          const textMaxW = contentWidth - innerPadX * 2 - 2;

          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(titleFS);
          const titleText = title || `${priority.charAt(0).toUpperCase()}${priority.slice(1)} note`;
          const titleLines = pdf.splitTextToSize(titleText, textMaxW);

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(bodyFS);
          const bodyLines = content ? pdf.splitTextToSize(content, textMaxW) : [];
          const metaLine = `${moment(n.date).format('MMM D, YYYY')}${author ? '  ' + author : ''}`;
          const metaLines = pdf.splitTextToSize(metaLine, textMaxW);

          const lineH = 4.5;
          const estH = innerPadY * 2 + titleLines.length * lineH + bodyLines.length * lineH + metaLines.length * 3.8 + 1;

          y = ensureSpace(y, estH + 2);

          pdf.setFillColor(252, 252, 252);
          pdf.setDrawColor(220, 220, 220);
          pdf.setLineWidth(0.2);
          pdf.roundedRect(margin, y, contentWidth, estH, 1, 1, 'FD');
          pdf.setFillColor(pr, pg, pb);
          pdf.rect(margin, y, 1.2, estH, 'F');

          let ty = y + innerPadY + lineH - 0.5;
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(titleFS);
          pdf.setTextColor(0, 0, 0);
          for (const line of titleLines) {
            pdf.text(line, margin + innerPadX + 2, ty);
            ty += lineH;
          }
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(bodyFS);
          pdf.setTextColor(40, 40, 40);
          for (const line of bodyLines) {
            pdf.text(line, margin + innerPadX + 2, ty);
            ty += lineH;
          }
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(7.5);
          pdf.setTextColor(120, 120, 120);
          for (const line of metaLines) {
            pdf.text(line, margin + innerPadX + 2, ty);
            ty += 3.8;
          }
          pdf.setTextColor(0, 0, 0);

          y += estH + 2;
        }

        return y;
      };

      // ── Draw notes section for month/week views ──
      const drawNotesSection = (startY: number): number => {
        if (notes.length === 0) return startY;
        let y = startY + 4;
        y = ensureSpace(y, 12);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(13);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Notes (${notes.length})`, margin, y + 4);
        y += 8;
        return drawNotesList(y, notes, /* includeDateHeader */ true);
      };

      // ── Compose ──
      let cursorY = drawHeader();
      if (isMonthView) {
        cursorY = drawMonthGrid(cursorY);
        if (notes.length > 0) {
          pdf.addPage();
          cursorY = drawHeader();
          cursorY = drawNotesSection(cursorY);
        }
      } else if (isWeekView) {
        cursorY = drawWeekGrid(cursorY);
        if (notes.length > 0) {
          pdf.addPage();
          cursorY = drawHeader();
          cursorY = drawNotesSection(cursorY);
        }
      } else {
        cursorY = drawDayList(cursorY);
      }

      // ── Save ──
      const dateStr = moment(selectedDate).format('YYYY-MM-DD');
      const filterStr = showOnlyMySchedules ? 'my-schedules' : 'all-schedules';
      const viewStr = isDayView ? 'day' : isWeekView ? 'week' : 'month';
      const filename = `shiftifi-calendar-${viewStr}-${dateStr}-${filterStr}.pdf`;
      pdf.save(filename);

      toast.success('Calendar exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export calendar');
    } finally {
      setIsExporting(false);
    }
  };

  /** Tasks that match a given schedule: an assignee matches the schedule's employee, and the shift type matches (or task has no shift type requirement). */
  const getScheduleTasks = (schedule: ScheduleWithDetails): TaskWithAssignees[] => {
    if (!showTasks) return [];
    return tasks.filter(t =>
      t.status !== 'completed' &&
      t.assignees.some(a => a.id === schedule.employee_id) &&
      (t.shift_type_id === null || t.shift_type_id === schedule.shift_type_id)
    );
  };

  // Convert schedules to calendar events (tasks are embedded inside events, not standalone)
  const events: CalendarEvent[] = useMemo(() => {
    let filteredSchedules = schedules;
    if (showOnlyMySchedules && user) {
      filteredSchedules = schedules.filter(s => s.employee_id === user.id);
    }
    return filteredSchedules.map(schedule => ({
      id: schedule.id,
      title: `${schedule.employee?.first_name} ${schedule.employee?.last_name} - ${schedule.shift_type?.name}`,
      start: new Date(schedule.start_datetime),
      end: new Date(schedule.end_datetime),
      resource: schedule,
      color: schedule.shift_type?.color || '#666',
    }));
  }, [schedules, showOnlyMySchedules, user]);

  // Get schedules for selected date
  const daySchedules = useMemo(() => {
    const startOfDay = moment(selectedDate).startOf('day').toDate();
    const endOfDay = moment(selectedDate).endOf('day').toDate();
    
    let filteredSchedules = schedules;
    
    // Filter to show only user's schedules if toggle is enabled
    if (showOnlyMySchedules && user) {
      filteredSchedules = schedules.filter(schedule => 
        schedule.employee_id === user.id
      );
    }
    
    return filteredSchedules
      .filter(schedule => {
        const scheduleStart = new Date(schedule.start_datetime);
        return scheduleStart >= startOfDay && scheduleStart <= endOfDay;
      })
      .sort((a, b) => {
        // Primary sort by start time
        const startDiff = new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime();
        if (startDiff !== 0) return startDiff;
        
        // Secondary sort by end time
        return new Date(a.end_datetime).getTime() - new Date(b.end_datetime).getTime();
      });
  }, [schedules, selectedDate, showOnlyMySchedules, user]);

  // Custom event component
  const EventComponent = ({ event }: { event: CalendarEvent }) => {
    // ── Schedule event ──
    const schedule = event.resource!;
    const tags = scheduleTags[schedule.id] || schedule.tags || [];
    const lightFill = lightenColor(event.color, 0.15);
    const borderColor = event.color;
    
    // Determine if this is a compact view (month view with many events)
    const isCompact = view === Views.MONTH;
    
    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          padding: isCompact ? '1px 3px' : '2px 4px',
          backgroundColor: lightFill,
          border: `2px solid ${borderColor}`,
          borderLeftWidth: isCompact ? '4px' : '2px',
          color: '#000',
          borderRadius: '4px',
          fontSize: isCompact ? '11px' : '12px',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          gap: isCompact ? '0px' : '2px',
          '&:hover': {
            backgroundColor: lightenColor(event.color, 0.25),
            transform: 'translateY(-1px)',
            boxShadow: `0 2px 4px ${lightenColor(event.color, 0.3)}`,
            zIndex: 10,
          },
        }}
        onClick={(e) => {
          e.stopPropagation();
          onScheduleClick?.(schedule);
        }}
      >
        <Typography 
          level="body-xs" 
          sx={{ 
            fontWeight: 'bold', 
            color: '#000',
            lineHeight: isCompact ? 1.2 : 1.4,
            fontSize: isCompact ? '11px' : '12px',
          }}
        >
          {schedule.employee?.first_name} {schedule.employee?.last_name}
        </Typography>
        {!isCompact && (
          <>
            <Typography level="body-xs" sx={{ color: '#000', fontSize: '11px' }}>
              {schedule.shift_type?.name}
            </Typography>
            <Chip
              size="sm"
              variant="soft"
              color={schedule.status === 'published' ? 'success' : 'warning'}
              sx={{ 
                mt: 0.5,
                fontSize: '10px',
                height: '16px',
                backgroundColor: lightenColor(borderColor, 0.3),
                color: '#000',
                border: `1px solid ${lightenColor(borderColor, 0.5)}`,
              }}
            >
              {schedule.status}
            </Chip>
            
            {/* Tags - only show in non-compact view */}
            {tags.length > 0 && (
              <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                {tags.slice(0, 2).map((scheduleTag: any) => {
                  const tag = scheduleTag.tag || scheduleTag;
                  return (
                    <TagChip
                      key={tag.id}
                      tag={tag}
                      size="sm"
                      deletable={false}
                      sx={{
                        fontSize: '8px',
                        height: '14px',
                        padding: '0 4px',
                        backgroundColor: `${tag.color}40`,
                        color: tag.color,
                        borderColor: `${tag.color}80`,
                        '&:hover': {
                          backgroundColor: `${tag.color}60`,
                        },
                      }}
                    />
                  );
                })}
                {tags.length > 2 && (
                  <Typography level="body-xs" sx={{ opacity: 0.8, color: '#000' }}>
                    +{tags.length - 2}
                  </Typography>
                )}
              </Box>
            )}

            {/* Embedded tasks for this user's schedule */}
            {(() => {
              const scheduleTasks = getScheduleTasks(schedule);
              if (scheduleTasks.length === 0) return null;
              return (
                <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {scheduleTasks.map(task => (
                    <Box
                      key={task.id}
                      onClick={e => { e.stopPropagation(); onTaskClick?.(task); }}
                      sx={{
                        fontSize: '10px',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: '3px',
                        backgroundColor: 'rgba(0,0,0,0.08)',
                        border: '1px dashed rgba(0,0,0,0.3)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: '#000',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.15)' },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontSize: '8px', lineHeight: 1, flexShrink: 0 }}
                      >
                        ✓
                      </Box>
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {task.name}
                      </Box>
                    </Box>
                  ))}
                </Box>
              );
            })()}
          </>
        )}

        {/* Compact (month) view: show task count badge */}
        {isCompact && (() => {
          const scheduleTasks = getScheduleTasks(schedule);
          if (scheduleTasks.length === 0) return null;
          return (
            <Box
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                fontSize: '9px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                lineHeight: 1,
              }}
            >
              {scheduleTasks.length}
            </Box>
          );
        })()}
      </Box>
    );
  };



  // Day view component
  const DayView = () => {
    const formatTime = (dateString: string) => {
      return moment(dateString).format('HH:mm');
    };

    const formatDate = (date: Date) => {
      return moment(date).format('dddd, MMMM D, YYYY');
    };

    // Only show the toggle for manager and employee users (not admin)
    const shouldShowToggle = user && user.role !== 'admin';

    // Get notes for the selected date
    const [dayNotes, setDayNotes] = useState<any[]>([]);

    useEffect(() => {
              const fetchDayNotes = async () => {
          try {
            const dateStr = moment(selectedDate).format('YYYY-MM-DD');
            const notes = await noteService.getNotesByDate(dateStr);
            setDayNotes(notes);
          } catch (error) {
            console.error('Error fetching day notes:', error);
          }
        };

      fetchDayNotes();
    }, [selectedDate]);

    return (
      <Box data-testid="day-view-container">
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography level="h4">
                {formatDate(selectedDate)}
              </Typography>
              <Stack direction="row" spacing={2}>
                <ButtonGroup size="sm">
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedDate(moment(selectedDate).subtract(1, 'day').toDate())}
                  >
                    Previous Day
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedDate(new Date())}
                  >
                    Today
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedDate(moment(selectedDate).add(1, 'day').toDate())}
                  >
                    Next Day
                  </Button>
                </ButtonGroup>
                <Stack direction="row" spacing={1}>
                  <Dropdown>
                    <MenuButton
                      size="sm"
                      variant="outlined"
                      startDecorator={<Download />}
                      endDecorator={<ArrowDropDown />}
                      loading={isExporting}
                    >
                      Export
                    </MenuButton>
                    <Menu>
                      <MenuItem onClick={exportToICS}>
                        Export as iCalendar
                      </MenuItem>
                      <MenuItem onClick={exportToPDF}>
                        Export as PDF
                      </MenuItem>
                    </Menu>
                  </Dropdown>
                  {hasPermission('schedules', 'create') && onAddSchedule && (
                    <Button
                      size="sm"
                      startDecorator={<Add />}
                      onClick={onAddSchedule}
                    >
                      Add Schedule
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Filter Toggle for Day View */}
        {shouldShowToggle && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="sm">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Person sx={{ fontSize: 16 }} />
                    <FormLabel sx={{ fontSize: 'sm', mb: 0 }}>
                      My Schedules Only
                    </FormLabel>
                    <Switch
                      checked={showOnlyMySchedules}
                      onChange={(e) => setShowOnlyMySchedules(e.target.checked)}
                      size="sm"
                    />
                  </Stack>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* View Navigation Buttons */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography level="body-sm" color="neutral">
                Switch to:
              </Typography>
              <ButtonGroup size="sm">
                <Button
                  variant="outlined"
                  startDecorator={<ViewWeek />}
                  onClick={() => setView(Views.WEEK)}
                >
                  Week View
                </Button>
                <Button
                  variant="outlined"
                  startDecorator={<CalendarViewMonth />}
                  onClick={() => setView(Views.MONTH)}
                >
                  Month View
                </Button>
              </ButtonGroup>
            </Stack>
          </CardContent>
        </Card>

        {/* Notes Section */}
        {dayNotes.length > 0 && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                Notes for this day
              </Typography>
              <Stack spacing={2}>
                {dayNotes.map((note) => (
                  <Box
                    key={note.id}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 'sm',
                      backgroundColor: 'background.level1'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          size="sm"
                          variant="soft"
                          sx={{
                            backgroundColor: note.priority === 'urgent' ? '#ef4444' : 
                                           note.priority === 'important' ? '#f59e0b' : '#3b82f6',
                            color: 'white',
                            fontSize: '0.625rem'
                          }}
                        >
                          {note.priority === 'urgent' ? '🚨 Urgent' : 
                           note.priority === 'important' ? '⚠️ Important' : '📝 Normal'}
                        </Chip>
                        <Chip
                          size="sm"
                          variant="outlined"
                          sx={{ fontSize: '0.625rem' }}
                        >
                          {note.visibility === 'public' ? '👁️ Public' : '🔒 Private'}
                        </Chip>
                      </Box>
                    </Box>
                    
                    {note.title && (
                      <Typography level="body-lg" fontWeight="md" sx={{ mb: 1 }}>
                        {note.title}
                      </Typography>
                    )}
                    
                    <Typography level="body-md" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                      {note.content}
                    </Typography>
                    
                    <Typography level="body-xs" color="neutral">
                      by {note.creator_name} • {moment(note.created_at).format('MMM D, YYYY h:mm A')}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        {daySchedules.length === 0 ? (
          <Card>
            <CardContent>
              <Stack alignItems="center" spacing={2} py={4}>
                <Typography level="h4" color="neutral">
                  No schedules for this day
                </Typography>
                <Typography level="body-sm" color="neutral" textAlign="center">
                  {hasPermission('schedules', 'create')
                    ? 'Create a schedule to get started.'
                    : 'No schedules are available for this day.'
                  }
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Sheet variant="outlined" sx={{ borderRadius: 'sm' }}>
            <Box sx={{ overflow: 'auto' }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 2fr', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ p: 2, fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                Employee
              </Box>
              <Box sx={{ p: 2, fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                Shift Type
              </Box>
              <Box sx={{ p: 2, fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                Start Time
              </Box>
              <Box sx={{ p: 2, fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                End Time
              </Box>
              <Box sx={{ p: 2, fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                Status
              </Box>
              <Box sx={{ p: 2, fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                Tags
              </Box>
              <Box sx={{ p: 2, fontWeight: 'bold' }}>
                Tasks
              </Box>
            </Box>
              
              {daySchedules.map((schedule) => (
                <Box
                  key={schedule.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 2fr',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'background.level1',
                    },
                  }}
                  onClick={() => onScheduleClick?.(schedule)}
                >
                  <Box sx={{ p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
                    <Typography level="body-md">
                      {schedule.employee?.first_name} {schedule.employee?.last_name}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: schedule.shift_type?.color,
                        }}
                      />
                      <Typography level="body-sm">
                        {schedule.shift_type?.name}
                      </Typography>
                    </Stack>
                  </Box>
                  <Box sx={{ p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
                    <Typography level="body-sm">
                      {formatTime(schedule.start_datetime)}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
                    <Typography level="body-sm">
                      {formatTime(schedule.end_datetime)}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
                    <Chip
                      size="sm"
                      color={schedule.status === 'published' ? 'success' : 'warning'}
                      variant="soft"
                    >
                      {schedule.status}
                    </Chip>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    {(() => {
                      const tags = scheduleTags[schedule.id] || schedule.tags || [];
                      return tags.length > 0 ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {tags.slice(0, 3).map((scheduleTag: any) => {
                            const tag = scheduleTag.tag || scheduleTag;
                            return (
                              <TagChip
                                key={tag.id}
                                tag={tag}
                                size="sm"
                                deletable={hasPermission('tags', 'remove')}
                                onDelete={hasPermission('tags', 'remove') ? async () => {
                                  try {
                                    await tagService.removeTagFromSchedule(schedule.id, tag.id);
                                    toast.success('Tag removed successfully');
                                    onTagsChange?.();
                                  } catch (error) {
                                    console.error('Error removing tag:', error);
                                    toast.error('Failed to remove tag');
                                  }
                                } : undefined}
                              />
                            );
                          })}
                          {tags.length > 3 && (
                            <Typography level="body-xs" color="neutral">
                              +{tags.length - 3}
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography level="body-xs" color="neutral">
                          No tags
                        </Typography>
                      );
                    })()}
                  </Box>
                  {/* Tasks cell */}
                  <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start' }} onClick={e => e.stopPropagation()}>
                    {(() => {
                      const scheduleTasks = getScheduleTasks(schedule);
                      if (scheduleTasks.length === 0) {
                        return (
                          <Typography level="body-xs" color="neutral">—</Typography>
                        );
                      }
                      return (
                        <Stack spacing={0.5}>
                          {scheduleTasks.map(task => (
                            <Chip
                              key={task.id}
                              size="sm"
                              variant="outlined"
                              sx={{
                                fontSize: '11px',
                                cursor: 'pointer',
                                borderStyle: 'dashed',
                                '&:hover': { backgroundColor: 'background.level2' },
                              }}
                              onClick={() => onTaskClick?.(task)}
                            >
                              {task.name}
                            </Chip>
                          ))}
                        </Stack>
                      );
                    })()}
                  </Box>
                </Box>
              ))}
            </Box>
          </Sheet>
        )}
      </Box>
    );
  };

  // Custom toolbar component
  const CustomToolbar = (toolbar: any) => {
    // Only show the toggle for manager and employee users (not admin)
    const shouldShowToggle = user && user.role !== 'admin';
    
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center">
              <ButtonGroup size="sm">
                <Button
                  variant={view === Views.WEEK ? 'solid' : 'outlined'}
                  startDecorator={<ViewWeek />}
                  onClick={() => {
                    setView(Views.WEEK);
                    toolbar.onView(Views.WEEK);
                  }}
                >
                  Week
                </Button>
                <Button
                  variant={view === Views.MONTH ? 'solid' : 'outlined'}
                  startDecorator={<CalendarViewMonth />}
                  onClick={() => {
                    setView(Views.MONTH);
                    toolbar.onView(Views.MONTH);
                  }}
                >
                  Month
                </Button>
                <Button
                  variant={view === 'day' ? 'solid' : 'outlined'}
                  startDecorator={<ViewDay />}
                  onClick={() => {
                    setView('day');
                    setSelectedDate(toolbar.date || new Date());
                  }}
                >
                  Day
                </Button>
              </ButtonGroup>
              
              <ButtonGroup size="sm">
                <Button
                  variant="outlined"
                  onClick={() => toolbar.onNavigate('PREV')}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => toolbar.onNavigate('TODAY')}
                >
                  Today
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => toolbar.onNavigate('NEXT')}
                >
                  Next
                </Button>
              </ButtonGroup>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              {shouldShowToggle && (
                <FormControl size="sm">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Person sx={{ fontSize: 16 }} />
                    <FormLabel sx={{ fontSize: 'sm', mb: 0 }}>
                      My Schedules Only
                    </FormLabel>
                    <Switch
                      checked={showOnlyMySchedules}
                      onChange={(e) => setShowOnlyMySchedules(e.target.checked)}
                      size="sm"
                    />
                  </Stack>
                </FormControl>
              )}
              
              <Typography level="h4">
                {toolbar.label}
              </Typography>

              <Stack direction="row" spacing={1}>
                <Dropdown>
                  <MenuButton
                    size="sm"
                    variant="outlined"
                    startDecorator={<Download />}
                    endDecorator={<ArrowDropDown />}
                    loading={isExporting}
                  >
                    Export
                  </MenuButton>
                  <Menu>
                    <MenuItem onClick={exportToICS}>
                      Export as iCalendar
                    </MenuItem>
                    <MenuItem onClick={exportToPDF}>
                      Export as PDF
                    </MenuItem>
                  </Menu>
                </Dropdown>

                {hasPermission('schedules', 'create') && onAddSchedule && (
                  <Button
                    size="sm"
                    startDecorator={<Add />}
                    onClick={onAddSchedule}
                  >
                    Add Schedule
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}>
      {/* Custom styles for overlapping events */}
      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            /* Improve visibility of overlapping events in month view */
            .rbc-month-view .rbc-event {
              margin: 1px 0;
              min-height: 20px;
              max-height: none;
            }
            
            /* Improve visibility of overlapping events in week view */
            .rbc-time-view .rbc-event {
              margin: 1px 2px;
              min-height: 20px;
            }
            
            /* Better spacing for multiple events in same slot */
            .rbc-event-content {
              display: flex;
              flex-direction: column;
              gap: 2px;
            }
            
            /* Ensure events are clickable even when overlapping */
            .rbc-event {
              z-index: 1;
              position: relative;
            }
            
            .rbc-event:hover {
              z-index: 10;
            }
            
            /* Improve month view cell styling for multiple events */
            .rbc-month-row {
              min-height: 100px;
            }
            
            .rbc-day-bg {
              cursor: pointer;
              transition: background-color 0.2s ease;
            }
            
            .rbc-day-bg:hover {
              background-color: rgba(0, 0, 0, 0.02);
            }
            
            /* Make date cells clickable in month view */
            .rbc-date-cell {
              cursor: pointer;
            }
            
            .rbc-date-cell:hover {
              background-color: rgba(0, 0, 0, 0.01);
            }
            
            /* Better visibility for event labels */
            .rbc-event-label {
              font-weight: 600;
            }
            
            /* Improve event list in month view */
            .rbc-event-allday {
              margin-bottom: 2px;
            }
            
            /* Better spacing between events in month view */
            .rbc-events-container {
              margin-right: 0;
            }
          `,
        }}
      />
      {view === 'day' ? (
        <DayView />
      ) : (
        <BigCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          view={view}
          onView={(newView) => setView(newView)}
          date={selectedDate}
          onNavigate={(newDate) => setSelectedDate(newDate)}
          components={{
            event: EventComponent,
            toolbar: CustomToolbar,
          }}
          selectable
          popup
          step={60}
          timeslots={1}
          defaultView={Views.WEEK}
          views={{
            week: true,
            month: true,
          }}
          style={{ height: '100%' }}
          onSelectSlot={(slotInfo) => {
            // When clicking on a day in month/week view, switch to day view
            if (slotInfo.action === 'click') {
              setSelectedDate(slotInfo.start);
              setView('day');
            }
          }}
          eventPropGetter={(event) => {
            const lightFill = lightenColor(event.color, 0.15);
            return {
              style: {
                backgroundColor: lightFill,
                border: 'none',
                borderRadius: '4px',
                color: '#000',
              },
            };
          }}
        />
      )}
      
      {/* Notes Modal */}
      <NotesModal
        open={notesModalOpen}
        onClose={handleNotesModalClose}
        date={selectedNoteDate}
        onNoteCreated={handleNoteChange}
        onNoteUpdated={handleNoteChange}
        onNoteDeleted={handleNoteChange}
      />
    </Box>
  );
} 